import {
  deleteMultipleCookiesViaAction,
  fetchAndSetUserSubscriptionStatus,
  getUserDataAction,
} from "@/actions/user.actions";
import {
  AppRoutesEnum,
  CookiesEnum,
  cookiesValues,
  SUBSCRIPTION_STATUS,
} from "@/constants/constants";
import {
  getDialect,
  getToken,
  shouldRedirectToSubscriptionFlow,
} from "@/utils/common.helpers";

/**
 * Handles post-login redirection logic.
 *
 * @param redirectionAfterLoginTo - The intended redirect URL after login
 * @param loginCallback - Callback function to update user context (from useUserContext().login)
 * @returns The final redirect URL, or null if an error occurred
 */
export const handlePostLoginRedirection = async (
  redirectionAfterLoginTo: string,
  loginCallback: (
    user: { mobileNumber: string; userId: string },
    subscriptionStatus: number,
  ) => void,
): Promise<string | null> => {
  try {
    const decodedRedirection = atob(
      decodeURIComponent(redirectionAfterLoginTo),
    );
    const [token, dialect] = await Promise.all([getToken(), getDialect()]);

    if (!token) {
      return `/${dialect}`;
    }

    const [{ subscriptionStatus: status }, userData] = await Promise.all([
      fetchAndSetUserSubscriptionStatus(true),
      getUserDataAction(),
    ]);

    const contactNumber = userData?.mobileNumber || "";
    const userId = userData?.userId || "";

    // Clear GamificationClaimTime cookie if user is subscribed or not trial eligible
    if (
      status === SUBSCRIPTION_STATUS.SUBSCRIBED ||
      status === SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED
    ) {
      await deleteMultipleCookiesViaAction([
        cookiesValues[CookiesEnum.GamificationClaimTime],
        cookiesValues[CookiesEnum.PaywallPage],
      ]);
    }

    if (userId) {
      loginCallback(
        {
          mobileNumber: contactNumber,
          userId,
        },
        status,
      );
    }

    if (decodedRedirection !== "") {
      const isCheckout = decodedRedirection.includes("checkout");
      if (isCheckout) {
        if (status === SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED) {
          if (shouldRedirectToSubscriptionFlow()) {
            return decodedRedirection;
          }
          // Preserve query params when redirecting to checkout
          const queryString = decodedRedirection.includes("?")
            ? decodedRedirection.substring(decodedRedirection.indexOf("?"))
            : "";
          return `${AppRoutesEnum.CHECKOUT}${queryString}`;
        } else if (status === SUBSCRIPTION_STATUS.SUBSCRIBED) {
          return `/${dialect}`;
        }
      }

      return decodedRedirection;
    }

    // Only redirect to paywall if no specific redirection was requested
    if (
      status === SUBSCRIPTION_STATUS.NOT_SUBSCRIBED ||
      status === SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED
    ) {
      return AppRoutesEnum.PAYWALL;
    }
  } catch (error) {
    console.error("Error in post-login redirection", error);
  }

  return AppRoutesEnum.COMMON_HOME;
};
